import type { GetPayV2Query, PayV2EntRespOvertime } from '@revelio/data-access';

import type { CompensationChartType } from '../../PageConfigProvider/types/chart';
import type { PayMetricValues } from '../../PageConfigProvider/types/metric';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../types';

type MultiCompensationData = {
  dim: string;
  data: GetPayV2Query['pay_v2'];
};

const getOvertimeMetricData = ({
  data,
  metric,
}: {
  data: PayV2EntRespOvertime;
  metric: PayMetricValues;
}): number[] => {
  switch (metric) {
    case 'base_salary':
    case 'base_salary_percentiles':
      return data?.SalaryBase || [];
    case 'total_compensation':
    case 'total_compensation_percentiles':
      return data?.SalaryTotal || [];
    case 'contractor_pay_rate':
    case 'contractor_pay_rate_percentiles':
      return data?.SalaryHourly || [];
    case 'total_spend_salary':
      return data?.SalaryBase || [];
    case 'total_spend_compensation':
      return data?.SalaryTotal || [];
    default:
      return [];
  }
};

export const convertCompensationTopNData = ({
  data,
  metric,
  chartType,
  groupBySegment,
  exchangeRate = 1,
}: {
  data: MultiCompensationData[];
  metric: PayMetricValues;
  chartType: CompensationChartType;
  groupBySegment?: boolean;
  exchangeRate?: number;
}): SingleDataSet | BreakdownSingleDataSet => {
  if (chartType === 'grouped-bar' || chartType === 'stacked-bar') {
    if (groupBySegment) {
      const groupedData = data?.reduce<Record<string, SingleDataPoint[]>>(
        (acc, subEntity) => {
          const subEntityDim = subEntity?.dim;
          subEntity?.data?.entities?.forEach((primaryEntity) => {
            const metadata = primaryEntity.metadata;
            const primaryDim = metadata?.short_name;
            const metricDataValues = getOvertimeMetricData({
              data: primaryEntity.metrics?.overtime,
              metric,
            });
            const lastTimeseriesValue =
              metricDataValues?.[metricDataValues.length - 1];
            const convertedValue =
              typeof lastTimeseriesValue === 'number'
                ? lastTimeseriesValue * exchangeRate
                : null;

            if (!acc[primaryDim]) acc[primaryDim] = [];
            acc[primaryDim].push({
              dim: subEntityDim,
              value: convertedValue,
              share: convertedValue,
            });
          });
          return acc;
        },
        {}
      );

      return {
        type: DataType.BREAKDOWN_SINGLE,
        data: Object.entries(groupedData).map(([dim, values]) => ({
          dim,
          breakdown: values,
        })),
      };
    }

    const groupedData: BreakdownSingleDataSet['data'] =
      data.map((primaryEntity): BreakdownData<SingleDataPoint> => {
        const breakdown: SingleDataPoint[] =
          primaryEntity?.data?.entities.map((subEntity): SingleDataPoint => {
            const metadata = subEntity.metadata;
            const overtimeData = subEntity.metrics?.overtime;
            const metricDataValues = getOvertimeMetricData({
              data: overtimeData,
              metric,
            });

            const lastTimeseriesValue =
              metricDataValues?.[metricDataValues.length - 1];
            const convertedValue =
              typeof lastTimeseriesValue === 'number'
                ? lastTimeseriesValue * exchangeRate
                : null;

            return {
              dim: metadata?.short_name,
              value: convertedValue,
              share: convertedValue,
            };
          }) ?? [];

        return {
          dim: primaryEntity.dim,
          breakdown,
        };
      }) ?? [];

    return { type: DataType.BREAKDOWN_SINGLE, data: groupedData };
  }

  const barData: SingleDataSet['data'] =
    data?.[0]?.data?.entities
      ?.map((entity) => {
        const metadata = entity.metadata;
        const overtimeData = entity.metrics?.overtime;
        const metricDataValues = getOvertimeMetricData({
          data: overtimeData,
          metric,
        });
        const lastTimeseriesValue =
          metricDataValues?.[metricDataValues.length - 1];
        const convertedValue =
          typeof lastTimeseriesValue === 'number'
            ? lastTimeseriesValue * exchangeRate
            : null;

        return {
          dim: metadata?.short_name,
          value: convertedValue,
        };
      })
      ?.sort((a, b) => (b.value ?? 0) - (a.value ?? 0)) ?? [];

  return { type: DataType.SINGLE, data: barData };
};
